import { useState, useEffect, useContext } from "react";
import { Card, Badge } from "flowbite-react";
import {
  Wifi,
  ArrowRight,
  User,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useParams, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import EvaluadoInfo from "../../../components/EvaludoInfo";
import { ThemeContext } from "../../../components/ThemeContext/ThemeContext";

export const MenuTipoModulo = () => {
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const { control, reset } = useForm();
  const location = useLocation();
  const [tipoEvaluado, setTipoEvaluado] = useState<string>("todos");
  const [isOpen, setIsOpen] = useState(false);
  const toggleBlind = () => setIsOpen(!isOpen);
  const { isDarkMode } = useContext(ThemeContext);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tipoParam = searchParams.get("tipo");

    if (tipoParam) {
      setTipoEvaluado(tipoParam);
      localStorage.setItem("ultimoTipoEvaluado", tipoParam);
    } else {
      const tipoGuardado = localStorage.getItem("ultimoTipoEvaluado");
      if (tipoGuardado) {
        setTipoEvaluado(tipoGuardado);
      }
    }
  }, [location.search]);

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut",
      },
    }),
  };

  const arrowVariants = {
    hidden: { x: 0 },
    hover: { x: 10, transition: { duration: 0.3 } },
  };

  const modules = [
    {
      title: "En linea",
      icon: Wifi,
      color: "blue",
      path: `/modulos/${evaluadosList_id}`,
    },
    {
      title: "Presencial",
      icon: User,
      color: "purple",
      path: `/modulos_presenciales/${evaluadosList_id}`,
    },
  ];

  // Estilos adaptativos según el tema
  const getBackgroundStyle = () => {
    return isDarkMode 
      ? "bg-gradient-to-br from-gray-900 to-gray-800" 
      : "bg-gradient-to-br from-customBlue/20 to-customCyan/20";
  };

  const getCardBackgroundStyle = (color: string) => {
    if (isDarkMode) {
      return color === "blue" 
        ? "from-blue-500 to-blue-600" 
        : "from-purple-500 to-purple-600";
    } else {
      return color === "blue" 
        ? "from-customBlue to-customCyan" 
        : "from-purple-400 to-purple-500";
    }
  };

  const getCardShadowStyle = (itemTitle: string, color: string) => {
    if (hoveredCard !== itemTitle) return "";
    
    if (isDarkMode) {
      return color === "blue" ? "shadow-blue-500/50" : "shadow-purple-500/50";
    } else {
      return color === "blue" ? "shadow-customCyan/50" : "shadow-purple-400/50";
    }
  };

  const getCardTextStyle = (color: string) => {
    return isDarkMode
      ? (color === "blue" ? "text-blue-100" : "text-purple-100")
      : (color === "blue" ? "text-blue-50" : "text-purple-50");
  };

  const getContainerStyle = () => {
    return isDarkMode
      ? "bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg"
      : "bg-customBlue/10 backdrop-filter backdrop-blur-lg";
  };

  const getHeadingTextStyle = () => {
    return isDarkMode ? "text-white" : "text-gray-800";
  };

  const getButtonStyle = () => {
    return isDarkMode
      ? "bg-gradient-to-r from-blue-600 to-purple-800 text-white"
      : "bg-gradient-to-r from-customBlue to-customCyan text-white";
  };

  const getCancelButtonStyle = () => {
    return isDarkMode ? "bg-red-500 text-white" : "bg-red-400 text-white";
  };

  return (
    <div className={`min-h-screen ${getBackgroundStyle()} grid place-items-center p-4`}>
      {/* Botón "Cancelar" */}
      <div className="flex justify-start items-center mb-8 w-full">
        <Link
          to={`/evaluadosList?tipo=${tipoEvaluado}`}
          onClick={() => window.scrollTo(0, 0)}
        >
          <motion.button
            className={`${getCancelButtonStyle()} py-2 px-4 rounded-lg`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Cancelar
          </motion.button>
        </Link>
      </div>

      {/* Botón de persiana */}
      <div className="flex justify-center">
        <motion.div className="mb-4 w-full max-w-[400px]">
          <motion.button
            onClick={toggleBlind}
            className={`w-[400px] text-lg font-semibold ${getButtonStyle()} py-3 rounded-lg focus:outline-none`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isOpen
              ? "Cerrar información del evaluado"
              : "Información del evaluado"}
          </motion.button>
        </motion.div>
      </div>

      {/* Componente de información del evaluado con control externo */}
      <EvaluadoInfo 
        externalIsOpen={isOpen} 
        externalToggle={toggleBlind}
        hideButton={true} 
      />

      <div className={`${getContainerStyle()} rounded-2xl p-8 shadow-2xl max-w-4xl w-full mt-6`}>
        <h2 className={`text-3xl font-bold ${getHeadingTextStyle()} mb-6 text-center`}>
          Forma de Evaluación
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {modules.map((item, index) => (
            <Link
              to={item.path}
              key={item.title}
              className="no-underline"
              onClick={() => window.scrollTo(0, 0)}
            >
              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                custom={index}
              >
                <Card
                  className={`bg-gradient-to-r cursor-pointer transition-all duration-300 ease-in-out transform hover:scale-105 
                    ${getCardBackgroundStyle(item.color)} border-none shadow-lg 
                    ${getCardShadowStyle(item.title, item.color)}`}
                  onMouseEnter={() => setHoveredCard(item.title)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className="flex items-center justify-between">
                    <item.icon className="text-white w-8 h-8 mb-4" />
                    <motion.div
                      variants={arrowVariants}
                      initial="hidden"
                      whileHover="hover"
                    >
                      <ArrowRight className="text-white w-6 h-6" />
                    </motion.div>
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-2">
                    {item.title}
                  </h3>
                  <p className={getCardTextStyle(item.color)}></p>
                </Card>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};